import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  DialogContent,
  Grid,
  DialogActions,
  Dialog,
  Avatar,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import StatusQueue from "../../../../Assets/Icons/status-queue.png";
import StatusHold from "../../../../Assets/Icons/status-hold.png";
import StatusInProgress from "../../../../Assets/Icons/status-in-progress.png";
import StatusComplete from "../../../../Assets/Icons/status-complete.png";
import OrderDetailConfirmationModal from "./ConfirmationModal";

const priorities = [
  { id: 1, label: "High", icon: "🏅" },
  { id: 2, label: "Medium" },
  { id: 3, label: "Low" }, // Also fixed the duplicate id issue
];

const pickers = [
  { id: 1, name: "Zayden Cole", avatar: "https://i.pravatar.cc/150?img=1" },
  { id: 2, name: "Olivia Hayes", avatar: "https://i.pravatar.cc/150?img=2" },
  { id: 3, name: "Mason Clarke", avatar: "https://i.pravatar.cc/150?img=3" },
  { id: 4, name: "Emma Johnson", avatar: "https://i.pravatar.cc/150?img=4" },
  { id: 5, name: "Liam Smith", avatar: "https://i.pravatar.cc/150?img=5" },
];

// Sample data for the "Order Line" table
const rows = [
  {
    id: 1,
    stockCode: "MDK1545NBF",
    description: "A.B.COVER RR C2-15-18 No Sen",
    binBayLocation: "Zone C - Aisle 2 - BAY C1",
    stockOnHand: 0,
    deliveryQty: 1,
    pickedQty: 0,
    status: "Stock on Hand",
  },
  {
    id: 2,
    stockCode: "ZA21540NBA",
    description: "Bar Cover Front Chrome",
    binBayLocation: "Zone C - Aisle 5 - BAY C0",
    stockOnHand: 25,
    deliveryQty: 1,
    pickedQty: 1,
    status: "Delivered to Bay",
  },
];

const renderActions = (params) => {
  if (params.row.status !== "Stock on Hand") return null;

  return (
    <>
      <Button
        variant="contained"
        sx={{
          border: "none",
          textTransform: "none",
          backgroundColor: "#3FC28A",
          "&:hover": { backgroundColor: "#08834e", color: "#495057" },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Inter",
            fontSize: "13px",
            fontWeight: 500,
            color: "#fff",
          }}
        >
          Stock Received
        </Typography>
      </Button>
      <Button
        variant="contained"
        sx={{
          border: "none",
          textTransform: "none",
          backgroundColor: "#1366D9",
          marginLeft: "16px",
          "&:hover": { backgroundColor: "#0345a0", color: "#495057" },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Inter",
            fontSize: "13px",
            fontWeight: 500,
            color: "#fff",
          }}
        >
          Remove This Item
        </Typography>
      </Button>
    </>
  );
};

const getColumns = (selectedOrder) => [
  { field: "stockCode", headerName: "Stock Code", width: 120 },
  { field: "description", headerName: "Description", width: 300 },
  { field: "binBayLocation", headerName: "Bin Bay Location", width: 200 },
  {
    field: "stockOnHand",
    headerName: "Stock on Hand",
    width: 140,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "deliveryQty",
    headerName: "Delivery Qty",
    width: 110,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "pickedQty",
    headerName: "Picked Qty",
    width: 110,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "status",
    headerName: "Status",
    width: 150,
    renderCell: (params) => {
      const status = params.value;
      let backgroundColor = "";
      let color = "black";

      if (status === "Stock on Hand") {
        color = "#F45B69";
      } else if (status === "Delivered to Bay") {
        color = "#3FC28A";
      }

      return (
        <Box
          sx={{
            backgroundColor,
            color,
            padding: "4px 8px",
            borderRadius: "4px",
            textAlign: "center",
            width: "100%",
          }}
        >
          {status}
        </Box>
      );
    },
  },
  ...(selectedOrder.status === "On Hold"
    ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 320,
          renderCell: (params) => renderActions(params, selectedOrder),
        },
      ]
    : []),
];

// Order details data (for the header section)
const orderDetails = {
  sssJobNumber: "V2763957",
  customer:
    "MS AUTO\nMS AUTOWORKS PTY LTD\n8 ARISTOC ROAD\nGLEN WAVERLEY\nVIC 3155",
  custOrder: "AGE091",
  priority: "High",
  date: "12/03/2025",
  time: "10:44:25",
  bay: "M6-BAY 6",
  cutoff: "04:00PM/10:30AM",
  picker: "Zayden Cole",
  status: "On Hold",
  phone: "0430847147",
  fax: "0430847147",
};
const OrderDetailsModal = ({ open, handleClose, selectedOrder }) => {
  const [priority, setPriority] = useState("");
  const [selectedPriority, setSelectedPriority] = useState(null);

  const [selectedPicker, setSelectedPicker] = useState(
    selectedOrder.picker.name
  );

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [type, setType] = useState("");

  const [loading, setLoading] = useState(false);

  if (!selectedOrder) return null;

  // Extract date and time from orderDate if available
  let formattedDate = "";
  let formattedTime = "";

  if (selectedOrder.orderDate) {
    const dateParts = selectedOrder.orderDate.split(" ");
    if (dateParts.length >= 1) {
      const dateComponents = dateParts[0].split("/");
      if (dateComponents.length === 3) {
        formattedDate = `${dateComponents[1]}/${dateComponents[0]}/${dateComponents[2]}`;
      } else {
        formattedDate = dateParts[0];
      }
    }

    if (dateParts.length >= 2) {
      formattedTime = dateParts[1];
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return StatusComplete;

      case "On Hold":
        return StatusHold;

      case "In Progress":
        return StatusInProgress;

      case "In Queue":
        return StatusQueue;

      default:
        return "";
    }
  };

  const handlePriorityChange = (event) => {
    const { value } = event.target;

    setSelectedPriority(value);
    setType("priority");
    setOpenConfirmationModal(true);
  };

  const handlePickerChange = (event) => {
    const { value } = event.target;

    setSelectedPicker(value);
    setType("picker");
    setOpenConfirmationModal(true);
  };

  const handleConfirm = () => {
    console.log("handle confirm");
  };

  const columns = getColumns(selectedOrder);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <DialogContent sx={{ p: 4, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                mb: 2,
                width: "80%",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  padding: "16px 24px",
                  borderRadius: "8px",
                  backgroundColor:
                    selectedOrder.status === "On Hold" ? "#F45B691A" : "#fff",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Lexend",
                      fontSize: "20px",
                      mb: 3,
                      fontWeight: 500,
                      color: "383E49",
                    }}
                  >
                    Order Detail
                  </Typography>
                </Box>

                <Box sx={{ width: "100%", marginTop: "16px" }}>
                  {/* Header Section: Order Detail */}

                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={3}
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ color: "#1570EF" }}>
                          SSS Job Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {orderDetails.sssJobNumber}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ color: "#1570EF" }}>
                          Cust Order
                        </Typography>
                        <Typography variant="body1">
                          {orderDetails.custOrder}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: "#1570EF" }}>
                          Priority
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "4px",
                          }}
                        >
                          <FormControl fullWidth>
                            <Select
                              value={priority || orderDetails.priority}
                              onChange={handlePriorityChange}
                              variant="outlined"
                              size="small"
                              renderValue={(selected) => {
                                const selectedPriority = priorities.find(
                                  (p) => p.label === selected
                                );
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {selectedPriority?.icon && (
                                      <span>{selectedPriority.icon}</span>
                                    )}
                                    {selectedPriority?.label}
                                  </Box>
                                );
                              }}
                            >
                              <MenuItem value="">Select Priority</MenuItem>
                              {priorities.map((priority) => (
                                <MenuItem
                                  key={priority.id}
                                  value={priority.label}
                                >
                                  {priority.icon && (
                                    <span style={{ marginRight: 8 }}>
                                      {priority.icon}
                                    </span>
                                  )}
                                  {priority.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Customer */}
                    <Grid item xs={12} sm={5} md={5} sx={{ display: "flex" }}>
                      <Divider orientation="vertical" sx={{ marginRight: 5 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: "#E19133" }}>
                          Customer
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {orderDetails.customer}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6c757d", mt: 1 }}
                        >
                          {orderDetails.phone}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                          Fax: {orderDetails.fax}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Right Column: Date, Time, Bay, Cutoff, Picker, Status */}
                    <Grid item xs={12} sm={3} md={4} sx={{ display: "flex" }}>
                      <Divider orientation="vertical" />
                      <Grid container spacing={1} sx={{ marginLeft: 4 }}>
                        <Grid
                          item
                          xs={6}
                          md={12}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Date :
                          </Typography>
                          <Typography variant="body1" textAlign="right">
                            {orderDetails.date}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          md={12}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Time :
                          </Typography>
                          <Typography variant="body1">
                            {orderDetails.time}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          md={12}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Bay :
                          </Typography>
                          <Typography variant="body1">
                            {selectedOrder.bay}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          md={12}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Cutoff :
                          </Typography>
                          <Typography variant="body1">
                            {selectedOrder.cutoff}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          md={12}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Picker :
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <FormControl fullWidth>
                              <Select
                                value={selectedPicker}
                                onChange={handlePickerChange}
                                variant="outlined"
                                size="small"
                                displayEmpty
                                renderValue={(selected) => {
                                  if (!selected) return "Select Picker";
                                  const picker = pickers.find(
                                    (p) => p.name === selected
                                  );
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Avatar
                                        alt={picker?.name}
                                        src={picker?.avatar}
                                        sx={{ width: 24, height: 24 }}
                                      />
                                      <Typography variant="body1">
                                        {picker?.name}
                                      </Typography>
                                    </Box>
                                  );
                                }}
                              >
                                <MenuItem disabled value="">
                                  Select Picker
                                </MenuItem>
                                {pickers.map((picker) => (
                                  <MenuItem key={picker.id} value={picker.name}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Avatar
                                        alt={picker.name}
                                        src={picker.avatar}
                                        sx={{ width: 24, height: 24 }}
                                      />
                                      {picker.name}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          md={12}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Status :
                          </Typography>
                          <Box
                            sx={{
                              color:
                                selectedOrder.status === "On Hold"
                                  ? "#F79009"
                                  : "default",
                              borderRadius: "4px",
                              display: "inline-block",
                            }}
                          >
                            <Typography variant="body1">
                              {selectedOrder.status}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                width: "20%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={getStatusIcon(selectedOrder.status)} alt="Hold Icon" />
            </Box>
          </Box>

          {/* Table Section: Order Line */}
          <Box
            sx={{ padding: "24px 16px", marginTop: 5, backgroundColor: "#fff" }}
          >
            <Box sx={{ position: "relative", width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#383E49", fontSize: "20px", fontWeight: 500 }}
                >
                  Order Line
                </Typography>
                {selectedOrder.status === "On Hold" && (
                  <Button
                    variant="outlined"
                    sx={{
                      border: "none",
                      textTransform: "none",
                      backgroundColor: "#949697",

                      "&:hover": { borderColor: "#495057", color: "#495057" },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#fff",
                        "&:hover": { color: "#121213" },
                      }}
                    >
                      Click to Mark Complete
                    </Typography>
                  </Button>
                )}
              </Box>

              <Box sx={{ width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSizeOptions={[5]} // Community version uses pageSizeOptions
                  disableColumnMenu // Optional: Disable column menu if not needed
                  getRowClassName={(params) =>
                    params.row.status === "Stock on Hand"
                      ? "stock-onhand-row"
                      : ""
                  }
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": {
                      color: "#383E49",
                      fontFamily: "Lexend",
                      fontWeight: 500,
                    },
                    "& .stock-onhand-row": {
                      backgroundColor: "#F45B691A",
                      color: "#F45B69",
                      "& .MuiDataGrid-cell": {
                        color: "#F45B69",
                        fontFamily: "Lexend",
                        fontWeight: 500,
                      },
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5", // Light gray header background
                      fontWeight: "bold",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 500,
                      color: "#667085",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ mr: 1, borderRadius: 1 }}
          >
            Close
          </Button>
          {/* {selectedOrder.status !== "On Hold" && (
            <Button
              variant="contained"
              sx={{
                borderRadius: 1,
                backgroundColor: "#3FC28A",
                "&:hover": {
                  backgroundColor: "#3aa880",
                },
              }}
            >
              Update
            </Button>
          )} */}
        </DialogActions>
      </Dialog>
      {openConfirmationModal && (
        <OrderDetailConfirmationModal
          open={openConfirmationModal}
          onCancel={() => setOpenConfirmationModal(false)}
          title={`${type === "priority" ? "Set Priority" : "Choose Picker"}`}
          header={`Are you sure want to ${
            type === "priority"
              ? `set Priority to ${selectedPriority}`
              : `change picker to ${selectedPicker}`
          }?`}
          loading={loading}
          handleConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default OrderDetailsModal;
